import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import api from 'api';
import type { NotificationInterface } from 'types';

/* ═══════════════════════════════════════════════════════
   Query keys
   ═══════════════════════════════════════════════════════ */

export const notificationKeys = {
    all: ['notifications'] as const,
    list: (userId: number, category?: string | null) =>
        ['notifications', 'list', userId, category ?? 'all'] as const,
    unread: (userId: number) => ['notifications', 'unread', userId] as const,
    categories: ['notifications', 'categories'] as const,
};

export type NotificationCategoryFilter = 'all' | string;

/* ═══════════════════════════════════════════════════════
   Queries — backed only by real endpoints:
   - GET /notifications
   - GET /notifications/unread
   - GET /notifications/categories
   - DELETE /notification?notification_id=
   ═══════════════════════════════════════════════════════ */

/**
 * GET /notifications
 * Optional `category` maps to BE query param `notification_category`.
 */
export const useNotifications = (
    userId: number,
    options?: { enabled?: boolean; category?: NotificationCategoryFilter }
) => {
    const category = options?.category && options.category !== 'all' ? options.category : undefined;

    return useQuery<NotificationInterface[]>({
        queryKey: notificationKeys.list(userId, category),
        queryFn: async () => {
            const params: Record<string, string> = {};
            if (category) {
                params.notification_category = category;
            }
            const { data } = await api.notification.get({ params });
            return (data as NotificationInterface[]) ?? [];
        },
        enabled: (options?.enabled ?? true) && !!userId,
        staleTime: 1 * 60 * 1000,
        refetchInterval: 2 * 60 * 1000,
        placeholderData: keepPreviousData,
    });
};

/** GET /notifications/unread → { is_unread: boolean } */
export const useNotificationsUnread = (userId: number, options?: { enabled?: boolean }) => {
    return useQuery<boolean>({
        queryKey: notificationKeys.unread(userId),
        queryFn: async () => {
            const { data } = await api.notification.getUnread();
            return Boolean(data?.is_unread);
        },
        enabled: (options?.enabled ?? true) && !!userId,
        staleTime: 1 * 60 * 1000,
        refetchInterval: 2 * 60 * 1000,
    });
};

/** GET /notifications/categories → NotificationType enum members */
export const useNotificationCategories = () => {
    return useQuery<string[]>({
        queryKey: notificationKeys.categories,
        queryFn: async () => {
            const { data } = await api.notification.getNotificationTypes();
            if (!Array.isArray(data)) return [];
            return data
                .map((item: unknown) => {
                    if (typeof item === 'string') return item;
                    if (item && typeof item === 'object' && 'name' in item) {
                        return String((item as { name: string }).name);
                    }
                    return String(item);
                })
                .filter(Boolean);
        },
        staleTime: 60 * 60 * 1000, // categories are stable
    });
};

/* ═══════════════════════════════════════════════════════
   Mutations
   ═══════════════════════════════════════════════════════ */

/** DELETE /notification?notification_id= */
export const useDeleteNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (notificationId: number) => {
            const { data } = await api.notification.deleteNotification(notificationId);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.all });
        },
    });
};
